import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Nav } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { upperFirst } from 'lodash';
import { t } from 'i18next';
import Table from '../../../components/Table';
import AllopathyModel from './allopathy-model';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '../../../utils/useDebounceHook';
import { getAllopathyDetails, getAllopathys } from '../../../redux/allopathys';
import { resetAllopathys } from '../../../redux/allopathys/slice';
import { useAllopathyColumn, useAllopathyType } from './hook';

const Allopathys = () => {
	const [title, setTitle] = useState('');
	const [model, setModel] = useState(false);
	const [type, setType] = useState('complaints');
	const [searchText, setSearchText] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(10);
	const [columnSort, setColumnSort] = useState({});
	const dispatch = useDispatch();
	const { loading, list, total } = useSelector(state => state.allopathys);

	const search = useDebounce(searchText, 500);
	const allopathysTypes = useAllopathyType(t);

	useEffect(() => {
		const lable = allopathysTypes.find((item) => item.value === type)?.label;
		setTitle(lable);
	}, [type]);

	useEffect(() => {
		fetchAllopathyByType();
		return () => {
			dispatch(resetAllopathys());
		};
	}, [currentPage, perPage, search, columnSort, type, dispatch]);

	const changeType = useCallback((type) => {
		setSearchText('');
		setPerPage(10);
		setCurrentPage(1);
		setColumnSort({});
		setType(type);
	}, []);

	const fetchAllopathyByType = useCallback(() => {
		dispatch(getAllopathys({ type, page: currentPage, per_page: perPage, search, ...columnSort }));
	}, [currentPage, perPage, search, type, columnSort, dispatch]);

	const handlePageChange = useCallback(page => {
		setCurrentPage(page);
	}, []);

	const handlePerRowsChange = useCallback(async(newPerPage) => {
		setPerPage(newPerPage);
		setCurrentPage(1);
	}, []);

	const handleSort = useCallback(
		(column, sortDirection) => {
			setColumnSort({ column_name: column.sortField, order: sortDirection });
		}, []);

	const fetchAllopathyDetails = useCallback(
		(id) => {
			dispatch(getAllopathyDetails({ id, type }))
				.unwrap()
				.then(() => setModel(true));
		},
		[dispatch, type]
	);

	const columns = useAllopathyColumn({ t, currentPage, perPage, handleEdit: fetchAllopathyDetails, title, type });

	return (
		<Card>
			<Card.Header className='bg-white fw-bold d-flex justify-content-between align-items-center'>
				<span>{title}</span>
				<div className='d-flex align-items-center'>
					<Form.Control
						type="search"
						placeholder={t('GENERAL.SEARCHTEXT')}
						size="sm"
						className='form-control d-inline-block w-auto me-2'
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
					/>
					<Button variant="success" onClick={() => setModel(true)} size="sm" className='d-inline-flex align-items-center'>
						<FaPlus className='me-1' /> {t('ALLOPATHY.ADD_BUTTON', { type: upperFirst(title) })}
					</Button>
				</div>
			</Card.Header>
			<Card.Body>
				<div style={{ overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap' }} >
					<Nav
						variant="tabs"
						activeKey={type}
						onSelect={(k) => changeType(k)}
						className="flex-nowrap"
					>
						{allopathysTypes.map((item) => (
							<Nav.Item key={item.value}>
								<Nav.Link eventKey={item.value}>{item.label}</Nav.Link>
							</Nav.Item>
						))}
					</Nav>
				</div>
				<Table
					columns={columns}
					data={list}
					loading={loading}
					handlePageChange={handlePageChange}
					perPage={perPage}
					handlePerRowsChange={handlePerRowsChange}
					total={total}
					handleSort={handleSort}
				/>
			</Card.Body>
			<AllopathyModel
				show={model}
				close={() => setModel(false)}
				fetch={fetchAllopathyByType}
				type={type}
				title={title}
			/>
		</Card>
	);
};

export default Allopathys;